import React, { useState } from 'react';
import { Card, CardContent, CardHeader } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { MATTERS } from '../lib/mock-data';
import { Search, Plus, Filter, FileText, AlertCircle, ChevronRight, Briefcase, User } from 'lucide-react';
import { useToast } from '../components/feedback/ToastProvider';
interface MattersProps {
  onNavigate: (page: string) => void;
}
export function Matters({
  onNavigate
}: MattersProps) {
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');
  const [searchQuery, setSearchQuery] = useState('');
  const {
    showToast
  } = useToast();
  const getStatusColor = (status: string): 'success' | 'secondary' | 'warning' | 'destructive' => {
    if (status.includes('Active')) return 'success';
    if (status.includes('Pre-Litigation')) return 'secondary';
    if (status.includes('Settlement')) return 'warning';
    if (status.includes('Trial')) return 'destructive';
    return 'secondary';
  };
  const filteredMatters = MATTERS.filter(m => m.name.toLowerCase().includes(searchQuery.toLowerCase()) || m.caseNumber.toLowerCase().includes(searchQuery.toLowerCase()) || m.type.toLowerCase().includes(searchQuery.toLowerCase()));
  const handleOpenMatter = (matterName: string) => {
    showToast({
      title: 'Opening matter workspace',
      description: `Loading ${matterName}.`,
      variant: 'info'
    });
    onNavigate('workspace');
  };
  return <div className="space-y-6" data-id="element-1370">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-1371">
        <div data-id="element-1372">
          <h1 className="text-2xl font-bold tracking-tight flex items-center" data-id="element-1373">
            <Briefcase className="w-6 h-6 mr-3 text-muted-foreground" data-id="element-1374" />
            Matters
          </h1>
          <p className="text-muted-foreground mt-1" data-id="element-1375">
            {MATTERS.length} active matters • {MATTERS.reduce((acc, m) => acc + m.pendingReviews, 0)} pending reviews
          </p>
        </div>
        <Button className="shrink-0" onClick={() => showToast({
        title: 'Matter intake is demo-only',
        description: 'Use an existing matter to continue the workflow demo.',
        variant: 'info'
      })} data-id="element-1376">
          <Plus className="w-4 h-4 mr-2" data-id="element-1377" />
          New Matter
        </Button>
      </div>

      {/* Filters & Search */}
      <Card data-id="element-1378">
        <CardHeader className="p-4 border-b" data-id="element-1379">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-1380">
            <div className="flex-1" data-id="element-1381">
              <Input placeholder="Search by matter name, case number, or type..." icon={<Search className="w-4 h-4" data-id="element-1383" />} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-1382" />
            </div>
            <div className="flex space-x-2" data-id="element-1384">
              <Button variant="outline" className="shrink-0" onClick={() => showToast({
              title: 'Filters coming next',
              description: 'Search and view modes are available now for narrowing matters.',
              variant: 'info'
            })} data-id="element-1385">
                <Filter className="w-4 h-4 mr-2" data-id="element-1386" />
                Filter
              </Button>
              <div className="inline-flex bg-muted p-1 rounded-md" role="tablist" aria-label="Matter view mode" data-id="element-1387">
                <button role="tab" aria-selected={viewMode === 'table'} onClick={() => setViewMode('table')} className={`px-3 py-1 text-xs font-medium rounded transition-colors ${viewMode === 'table' ? 'bg-background shadow-sm' : 'text-muted-foreground'}`} data-id="element-1388">
                  Table
                </button>
                <button role="tab" aria-selected={viewMode === 'cards'} onClick={() => setViewMode('cards')} className={`px-3 py-1 text-xs font-medium rounded transition-colors ${viewMode === 'cards' ? 'bg-background shadow-sm' : 'text-muted-foreground'}`} data-id="element-1389">
                  Cards
                </button>
              </div>
            </div>
          </div>
        </CardHeader>

        {viewMode === 'table' ? <CardContent className="p-0" data-id="element-1390">
            <div className="overflow-x-auto" data-id="element-1391">
              <table className="w-full text-sm text-left" data-id="element-1392">
                <thead className="text-xs text-muted-foreground uppercase bg-muted/50 border-b" data-id="element-1393">
                  <tr data-id="element-1394">
                    <th className="px-4 py-3 font-medium" data-id="element-1395">Case # / Matter</th>
                    <th className="px-4 py-3 font-medium" data-id="element-1396">Type</th>
                    <th className="px-4 py-3 font-medium" data-id="element-1397">Lead Attorney</th>
                    <th className="px-4 py-3 font-medium" data-id="element-1398">Status</th>
                    <th className="px-4 py-3 font-medium text-center" data-id="element-1399">Docs</th>
                    <th className="px-4 py-3 font-medium text-center" data-id="element-1400">Reviews</th>
                    <th className="px-4 py-3 font-medium" data-id="element-1401">Last Activity</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-1402">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y" data-id="element-1403">
                  {filteredMatters.map(matter => <tr key={matter.id} className="hover:bg-muted/30 transition-colors cursor-pointer group" onClick={() => handleOpenMatter(matter.name)} data-id="element-1404">
                      <td className="px-4 py-4" data-id="element-1405">
                        <div data-id="element-1406">
                          <p className="font-mono text-xs text-muted-foreground mb-0.5" data-id="element-1407">{matter.caseNumber}</p>
                          <p className="font-medium text-blue-600 group-hover:underline" data-id="element-1408">{matter.name}</p>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-muted-foreground" data-id="element-1409">{matter.type}</td>
                      <td className="px-4 py-4" data-id="element-1410">
                        <div className="flex items-center space-x-2" data-id="element-1411">
                          <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-[10px] font-medium" data-id="element-1412">
                            {matter.attorney.split(' ').map(n => n[0]).join('')}
                          </div>
                          <span className="text-muted-foreground" data-id="element-1413">{matter.attorney}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4" data-id="element-1414">
                        <Badge variant={getStatusColor(matter.status)} data-id="element-1415">{matter.status}</Badge>
                      </td>
                      <td className="px-4 py-4 text-center" data-id="element-1416">
                        <div className="flex items-center justify-center space-x-1" data-id="element-1417">
                          <FileText className="w-3 h-3 text-muted-foreground" data-id="element-1418" />
                          <span className="font-medium" data-id="element-1419">{matter.documentsCount.toLocaleString()}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-center" data-id="element-1420">
                        {matter.pendingReviews > 0 ? <Badge variant="warning" className="text-[10px]" data-id="element-1421">
                            <AlertCircle className="w-3 h-3 mr-1" data-id="element-1422" />
                            {matter.pendingReviews}
                          </Badge> : <span className="text-muted-foreground text-xs" data-id="element-1423">—</span>}
                      </td>
                      <td className="px-4 py-4 text-muted-foreground text-xs" data-id="element-1424">{matter.lastUpdated}</td>
                      <td className="px-4 py-4 text-right" data-id="element-1425">
                        <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => {
                    e.stopPropagation();
                    handleOpenMatter(matter.name);
                  }} data-id="element-1426">
                          Open <ChevronRight className="w-4 h-4 ml-1" data-id="element-1427" />
                        </Button>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </CardContent> : <CardContent className="p-4" data-id="element-1428">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1429">
              {filteredMatters.map(matter => <Card key={matter.id} className="hover:border-blue-300 transition-colors cursor-pointer group" onClick={() => handleOpenMatter(matter.name)} data-id="element-1430">
                  <CardContent className="p-4" data-id="element-1431">
                    <div className="flex items-start justify-between mb-3" data-id="element-1432">
                      <Badge variant={getStatusColor(matter.status)} className="text-[10px]" data-id="element-1433">
                        {matter.status}
                      </Badge>
                      {matter.pendingReviews > 0 && <Badge variant="warning" className="text-[10px]" data-id="element-1434">
                          <AlertCircle className="w-3 h-3 mr-1" data-id="element-1435" />
                          {matter.pendingReviews} reviews
                        </Badge>}
                    </div>
                    <p className="font-mono text-xs text-muted-foreground mb-1" data-id="element-1436">{matter.caseNumber}</p>
                    <h3 className="font-semibold text-foreground group-hover:text-blue-600 transition-colors mb-2 line-clamp-2" data-id="element-1437">
                      {matter.name}
                    </h3>
                    <p className="text-xs text-muted-foreground mb-3" data-id="element-1438">{matter.type}</p>
                    <div className="flex items-center justify-between text-xs text-muted-foreground pt-3 border-t" data-id="element-1439">
                      <div className="flex items-center space-x-1" data-id="element-1440">
                        <User className="w-3 h-3" data-id="element-1441" />
                        <span data-id="element-1442">{matter.attorney}</span>
                      </div>
                      <div className="flex items-center space-x-1" data-id="element-1443">
                        <FileText className="w-3 h-3" data-id="element-1444" />
                        <span data-id="element-1445">{matter.documentsCount.toLocaleString()} docs</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>)}
            </div>
          </CardContent>}
      </Card>
    </div>;
}